import { useEffect } from 'react';
import { toast } from 'sonner';
import {
    getCurrentPushSubscription,
    subscribeToPush,
} from '../lib/pwaInstall';
import { savePushSubscription } from '../lib/notificationPreferences';
import { syncPermissionState } from '../lib/notificationPermission';

/**
 * On every app load with an authenticated user, run two passes:
 *
 * 1. `syncPermissionState` — keep `user_notification_browser_state` in
 *    sync with the live `Notification.permission` value. On the
 *    denied→granted transition, also silently re-subscribe and show the
 *    user a toast confirming push is back.
 *
 * 2. Defensive re-save of an existing subscription. Covers iPhone users
 *    completing Add-to-Home-Screen + grant outside the modal, and
 *    re-installs / new devices where the backend row may be missing.
 *
 * Silent on every failure path. The reminder cron's email fallback
 * handles users who never get a push subscription.
 */
export function usePushSubscriptionSync(userId: string | null | undefined) {
    useEffect(() => {
        if (!userId) return;
        if (typeof window === 'undefined') return;
        if (!('Notification' in window)) return;
        if (!('serviceWorker' in navigator) || !('PushManager' in window)) {
            return;
        }

        const vapidKey = import.meta.env.VITE_VAPID_PUBLIC_KEY as
            | string
            | undefined;

        let cancelled = false;
        (async () => {
            try {
                const result = await syncPermissionState();
                if (cancelled) return;
                if (result.changed && result.to === 'granted') {
                    toast.success('Avisos por móvil activados.');
                }
            } catch (err) {
                console.warn(
                    '[usePushSubscriptionSync] permission sync failed:',
                    err,
                );
            }

            // Subscribe pass — only if permission is currently granted and
            // we haven't already saved a subscription via syncPermissionState.
            if (Notification.permission !== 'granted') return;
            if (!vapidKey) return;

            try {
                const existing = await getCurrentPushSubscription();
                if (cancelled) return;

                const subscription =
                    existing ?? (await subscribeToPush(vapidKey));
                if (!subscription || cancelled) return;

                await savePushSubscription(subscription);
            } catch (err) {
                console.warn(
                    '[usePushSubscriptionSync] subscribe pass failed:',
                    err,
                );
            }
        })();

        return () => {
            cancelled = true;
        };
    }, [userId]);
}
